export type PointerPoint = { x: number; y: number };

export type EditorGestureState =
  | "IDLE"
  | "MOVE"
  | "RESIZE_START"
  | "RESIZE_END"
  | "PAN"
  | "ZOOM"
  | "DRAW"
  | "VOICE_DRAW"
  | "VOICE_REPITCH";

export const EDITOR_MIN_MIDI = 0;
export const EDITOR_MAX_MIDI = 127;
export const PITCH_ROW_HEIGHT = 27;
export const EDITOR_ZOOM_LEVELS = [0.5, 1, 2, 4] as const;
export const MIN_EDITOR_ZOOM = EDITOR_ZOOM_LEVELS[0];
export const MAX_EDITOR_ZOOM = EDITOR_ZOOM_LEVELS.at(-1) as number;
export const EDITOR_PIXELS_PER_BEAT = 58;
export const EDITOR_MIN_TIMELINE_WIDTH = 680;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function clampEditorZoom(zoom: number): number {
  return clamp(Number.isFinite(zoom) ? zoom : MIN_EDITOR_ZOOM, MIN_EDITOR_ZOOM, MAX_EDITOR_ZOOM);
}

export function editorTimelineWidth(totalBeats: number, zoom: number): number {
  const baseWidth = Math.max(EDITOR_MIN_TIMELINE_WIDTH, Math.max(0, totalBeats) * EDITOR_PIXELS_PER_BEAT);
  return baseWidth * clampEditorZoom(zoom);
}

export function beatToTimelineX(beat: number, totalBeats: number, contentWidth: number): number {
  if (totalBeats <= 0 || contentWidth <= 0) return 0;
  return clamp(beat / totalBeats, 0, 1) * contentWidth;
}

export function timelineXToBeat(x: number, totalBeats: number, contentWidth: number): number {
  if (totalBeats <= 0 || contentWidth <= 0) return 0;
  return clamp(x / contentWidth, 0, 1) * totalBeats;
}

export function horizontalDragToBeatDelta(deltaX: number, totalBeats: number, contentWidth: number): number {
  if (totalBeats <= 0 || contentWidth <= 0) return 0;
  return deltaX / contentWidth * totalBeats;
}

export function zoomFromWheel(currentZoom: number, deltaY: number): number {
  return nextEditorZoomLevel(currentZoom, deltaY < 0 ? 1 : -1);
}

export function nextEditorZoomLevel(currentZoom: number, direction: -1 | 1): number {
  const current = clampEditorZoom(currentZoom);
  if (direction > 0) return EDITOR_ZOOM_LEVELS.find((level) => level > current + 0.001) ?? MAX_EDITOR_ZOOM;
  return [...EDITOR_ZOOM_LEVELS].reverse().find((level) => level < current - 0.001) ?? MIN_EDITOR_ZOOM;
}

export function scrollLeftForZoomAnchor(options: {
  anchorBeat: number;
  viewportX: number;
  totalBeats: number;
  contentWidth: number;
  viewportWidth: number;
}): number {
  const desired = beatToTimelineX(options.anchorBeat, options.totalBeats, options.contentWidth) - options.viewportX;
  return clamp(desired, 0, Math.max(0, options.contentWidth - options.viewportWidth));
}

export function moveMidiByPointer(options: {
  originalMidi: number;
  startClientY: number;
  currentClientY: number;
  startScrollTop: number;
  currentScrollTop: number;
  rowHeight?: number;
}): number {
  const rowHeight = options.rowHeight ?? PITCH_ROW_HEIGHT;
  const travelled = options.currentClientY - options.startClientY
    + options.currentScrollTop - options.startScrollTop;
  return Math.max(EDITOR_MIN_MIDI, Math.min(EDITOR_MAX_MIDI,
    options.originalMidi - Math.round(travelled / Math.max(1, rowHeight))));
}

export function pitchEdgeAutoPan(clientY: number, top: number, bottom: number, edge = 44): number {
  if (clientY < top + edge) return -Math.ceil((top + edge - clientY) / 5);
  if (clientY > bottom - edge) return Math.ceil((clientY - (bottom - edge)) / 5);
  return 0;
}

export function nextIdleGesture(previous: EditorGestureState): EditorGestureState {
  void previous;
  return "IDLE";
}
export type ScrollPoint = { left: number; top: number };

export function pointerCentroid(points: Iterable<PointerPoint>): PointerPoint | null {
  let count = 0;
  let x = 0;
  let y = 0;
  for (const point of points) {
    x += point.x;
    y += point.y;
    count += 1;
  }
  return count ? { x: x / count, y: y / count } : null;
}

export function panScrollFromCentroid(scroll: ScrollPoint, previous: PointerPoint, next: PointerPoint): ScrollPoint {
  return { left: scroll.left - (next.x - previous.x), top: scroll.top - (next.y - previous.y) };
}

export function isDesktopPanButton(pointerType: string, button: number): boolean {
  return pointerType === "mouse" && (button === 1 || button === 2);
}